// Build a unified day plan: past events (logged) + active event (live) + future
// predictions (upcoming dashed bars). Drives DayRibbon, TrackCards, DayTimeline.

import type { EventRow } from "./events";
import type { AgeBand } from "../data/tcbSchedule";
import {
  predictNextFeed,
  predictNextNap,
  inferTempGroup,
  type Basis,
  type TempGroup,
} from "./schedule";
import { sleepEndMs, sleepMinutesInWindow, sleepOverlapsWindow, startOfLocalDay } from "./events";
import { minutesFromMidnight } from "./timeFmt";

export type PlanKind = "feed" | "nurse" | "sleep" | "bedtime" | "wake";
export type PlanStatus = "logged" | "live" | "upcoming";

export type PlanEvent = {
  id: string;
  kind: PlanKind;
  status: PlanStatus;
  // Minutes from start-of-local-day.
  start: number;
  end?: number;
  // Logged-event extras.
  amountOz?: number;
  feedMethod?: "bottle" | "breast";
  durationMin?: number;
  note?: string;
  /** Sleep only: this sleep began on an earlier day, so `start` is clipped to midnight. */
  continuedFromPrevDay?: boolean;
  /** Sleep only: true wall-clock start, even when it fell on an earlier day. */
  actualStartAt?: string;
  /** Sleep only: full duration across midnight, vs `end - start` which is clipped to this day. */
  totalDurationMin?: number;
  // Upcoming-event extras.
  predicted?: {
    start: number;
    rangeStartMin?: number;
    rangeEndMin?: number;
    dur?: number;
    amountOz?: number;
    amountRange?: [number, number];
    basis?: Basis;
    confidence?: "low" | "med" | "high";
    label?: string;
  };
  // Original event row, if any. Used by edit affordances later.
  row?: EventRow;
};

export type DayPlan = {
  /** Minutes from midnight for "start of day" — first logged event, or 7am if empty. */
  dayStartMin: number;
  /** Minutes from midnight for "bedtime + 30min" so the ribbon has tail padding. */
  dayEndMin: number;
  /** Minutes from midnight for `now`. Anchors the ribbon's vertical line. */
  nowMin: number;
  /** Logged + live + upcoming. Sorted by start. */
  events: PlanEvent[];
};

// Default wake/bedtime if we can't derive from data — newborn-friendly defaults
// (matches PRD §5.1 sample 6:55 → 8:30+30).
const DEFAULT_DAY_START_MIN = 7 * 60;
const DEFAULT_BEDTIME_MIN = 19 * 60 + 30;

function bandFeedIntervalMin(band: AgeBand): number {
  return Math.round(((band.feedIntervalHrsMin + band.feedIntervalHrsMax) / 2) * 60);
}

function bandWakeWindowMin(band: AgeBand): number {
  return Math.round((band.wakeWindowMin + band.wakeWindowMax) / 2);
}

function parseHHMM(s: string | undefined, fallback: number): number {
  const m = s?.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return fallback;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

// Temperament nudges the bedtime anchor: an early-settling baby toward the
// earliest bedtime, a late-cluster baby toward the latest, else the midpoint.
function bedtimeMinFor(band: AgeBand, tempGroup: TempGroup): number {
  const early = parseHHMM(band.bedtimeEarliest, DEFAULT_BEDTIME_MIN);
  const late = parseHHMM(band.bedtimeLatest, DEFAULT_BEDTIME_MIN);
  if (tempGroup === "group-1") return early;
  if (tempGroup === "group-2") return late;
  return Math.round((early + late) / 2);
}

/**
 * A sleep's slice of one local day. A sleep is an interval, so it can belong to
 * two days at once: the night sleep that starts at 11:40pm shows on that evening
 * clipped at midnight, and again the next morning starting at midnight. Both
 * slices carry the true start time and full duration for display.
 */
function sleepSlice(
  row: EventRow,
  dayStartMs: number,
  dayEndMsExclusive: number,
  now: Date,
): PlanEvent {
  const startMs = new Date(row.at).getTime();
  const endMs = sleepEndMs(row, now);
  const continuedFromPrevDay = startMs < dayStartMs;
  // Minutes from *this* day's midnight — 0 when the sleep began yesterday.
  const start = continuedFromPrevDay ? 0 : minutesFromMidnight(new Date(row.at));
  const sliceMin = sleepMinutesInWindow(row, dayStartMs, dayEndMsExclusive, now);
  // Real elapsed time, not day-minute arithmetic: a sleep that ends on the next
  // day would make end-minutes minus start-minutes go negative.
  const totalDurationMin = Math.max(1, Math.round((endMs - startMs) / 60_000));
  return {
    id: row.id,
    kind: "sleep",
    status: row.end_at == null ? "live" : "logged",
    start,
    end: Math.min(start + Math.max(1, sliceMin), 24 * 60),
    durationMin: totalDurationMin,
    totalDurationMin,
    continuedFromPrevDay,
    actualStartAt: row.at,
    row,
  };
}

/** Rows belonging to one local day: sleeps by interval overlap, everything else by timestamp. */
function rowsForDay(
  events: EventRow[],
  dayStartMs: number,
  dayEndMsExclusive: number,
  now: Date,
): EventRow[] {
  return events.filter((e) => {
    if (e.type === "sleep") return sleepOverlapsWindow(e, dayStartMs, dayEndMsExclusive, now);
    const t = new Date(e.at).getTime();
    return t >= dayStartMs && t < dayEndMsExclusive;
  });
}

/**
 * For a past day: just logged events, no predictions, no upcoming dashed bars.
 * Used by PastDayView.
 */
export function buildPastDayPlan(events: EventRow[], date: Date, now: Date = new Date()): DayPlan {
  const dayStartMs = startOfLocalDay(date).getTime();
  const dayEndMsExclusive = dayStartMs + 86_400_000;

  const todayRows = rowsForDay(events, dayStartMs, dayEndMsExclusive, now);

  const planEvents: PlanEvent[] = [];
  for (const row of todayRows) {
    const startMin = minutesFromMidnight(new Date(row.at));
    if (row.type === "sleep") {
      planEvents.push({ ...sleepSlice(row, dayStartMs, dayEndMsExclusive, now), status: "logged" });
    } else if (row.type === "feed" || row.type === "nurse") {
      planEvents.push({
        id: row.id,
        kind: row.type === "feed" ? "feed" : "nurse",
        status: "logged",
        start: startMin,
        end: startMin + (row.duration_min ?? 15),
        amountOz: row.amount_oz ?? undefined,
        feedMethod: row.type === "feed" ? "bottle" : "breast",
        durationMin: row.duration_min ?? undefined,
        row,
      });
    }
  }

  const sorted = planEvents.sort((a, b) => a.start - b.start);
  const firstStart = sorted.length ? sorted[0].start : DEFAULT_DAY_START_MIN;
  const lastEnd = sorted.length ? Math.max(...sorted.map((e) => e.end ?? e.start)) : DEFAULT_BEDTIME_MIN;
  return {
    dayStartMin: Math.min(firstStart, DEFAULT_DAY_START_MIN),
    dayEndMin: Math.max(lastEnd + 30, DEFAULT_BEDTIME_MIN),
    nowMin: 24 * 60, // past noon marker: place at right end so the now-dot is off-screen
    events: sorted,
  };
}

export function buildDayPlan(
  events: EventRow[],
  band: AgeBand,
  now: Date = new Date(),
): DayPlan {
  const dayStartMs = startOfLocalDay(now).getTime();
  const dayEndMsExclusive = dayStartMs + 86_400_000;

  // Filter to today's logged events.
  const todayRows = rowsForDay(events, dayStartMs, dayEndMsExclusive, now);

  const planEvents: PlanEvent[] = [];

  for (const row of todayRows) {
    const startMin = minutesFromMidnight(new Date(row.at));
    if (row.type === "sleep") {
      planEvents.push(sleepSlice(row, dayStartMs, dayEndMsExclusive, now));
    } else if (row.type === "feed" || row.type === "nurse") {
      planEvents.push({
        id: row.id,
        kind: row.type === "feed" ? "feed" : "nurse",
        status: "logged",
        start: startMin,
        end: startMin + (row.duration_min ?? (row.type === "feed" ? 15 : 20)),
        amountOz: row.amount_oz ?? undefined,
        feedMethod: row.type === "feed" ? "bottle" : "breast",
        durationMin: row.duration_min ?? undefined,
        row,
      });
    }
    // diaper / weight / etc. don't show on the ribbon.
  }

  // Day start — earliest logged event today, or default.
  const firstStart = planEvents.length ? Math.min(...planEvents.map((e) => e.start)) : DEFAULT_DAY_START_MIN;
  const dayStartMin = Math.min(firstStart, DEFAULT_DAY_START_MIN);

  // Bedtime + 30 for tail padding; temperament shifts the anchor.
  const tempGroup = inferTempGroup(events, now);
  const bedtimeMin = bedtimeMinFor(band, tempGroup);
  const dayEndMin = bedtimeMin + 30;

  // Add upcoming predictions.
  const nowMin = minutesFromMidnight(now);
  const upcoming: PlanEvent[] = [];

  // Determine active sleep (live), if any.
  const liveSleep = planEvents.find((e) => e.status === "live");
  if (!liveSleep) {
    // Predict next nap (if not currently asleep + we have a last sleep).
    const nap = predictNextNap(events, band, now);
    if (nap && nowMin < bedtimeMin && minutesFromMidnight(nap.at) < bedtimeMin) {
      upcoming.push({
        id: `pred-nap-${nap.at.toISOString()}`,
        kind: "sleep",
        status: "upcoming",
        start: minutesFromMidnight(nap.at),
        predicted: {
          start: minutesFromMidnight(nap.at),
          rangeStartMin: minutesFromMidnight(nap.rangeMin),
          rangeEndMin: minutesFromMidnight(nap.rangeMax),
          dur: 75,
          basis: nap.basis,
          confidence: nap.confidence,
        },
      });
    }
  }

  // Predict next feed.
  const feed = predictNextFeed(events, band, now);
  if (feed && minutesFromMidnight(feed.at) > nowMin && minutesFromMidnight(feed.at) < bedtimeMin) {
    upcoming.push({
      id: `pred-feed-${feed.at.toISOString()}`,
      kind: "feed",
      status: "upcoming",
      start: minutesFromMidnight(feed.at),
      predicted: {
        start: minutesFromMidnight(feed.at),
        rangeStartMin: minutesFromMidnight(feed.rangeMin),
        rangeEndMin: minutesFromMidnight(feed.rangeMax),
        amountOz: feed.amountOzMid,
        amountRange: feed.amountOzMin != null && feed.amountOzMax != null ? [feed.amountOzMin, feed.amountOzMax] : undefined,
        basis: feed.basis,
        confidence: feed.confidence,
      },
    });
  }

  // Fill out remaining upcoming feeds + naps until bedtime, alternating, at band cadence.
  const feedGap = bandFeedIntervalMin(band);
  const wakeGap = bandWakeWindowMin(band);
  let lastFeedMin = upcoming.find((e) => e.kind === "feed")?.start
    ?? planEvents.filter((e) => e.kind === "feed" || e.kind === "nurse").map((e) => e.start).sort((a, b) => b - a)[0]
    ?? nowMin;
  // Anchor the nap fill no earlier than now. A sleep carried over from last
  // night has `start` clipped to midnight, so anchoring on it would schedule
  // "upcoming" naps back at 1am — in the past, and during the sleep itself.
  const firstNap = upcoming.find((e) => e.kind === "sleep");
  // Future slots are hypothetical. Include the full predicted sleep before
  // adding awake time; if overdue, keep its original target visible above.
  let lastSleepEndMin = firstNap
    ? Math.max(firstNap.start, nowMin) + (firstNap.predicted?.dur ?? 75)
    : liveSleep ? Math.max(liveSleep.start + 75, nowMin) : null;

  // Cap follow-ups so we don't generate dozens. The cursors always advance —
  // only rendering is skipped for a slot that already passed, otherwise a
  // behind-schedule cursor would stall the loop and produce nothing.
  for (let i = 0; i < 4; i++) {
    const nextFeed = lastFeedMin + feedGap;
    if (nextFeed < bedtimeMin) {
      if (nextFeed > nowMin) {
        upcoming.push({
          id: `pred-feed-fill-${i}`,
          kind: "feed",
          status: "upcoming",
          start: nextFeed,
          predicted: { start: nextFeed, basis: "tcb" },
        });
      }
      lastFeedMin = nextFeed;
    }
    const nextNap = lastSleepEndMin == null ? Infinity : lastSleepEndMin + wakeGap;
    if (nextNap < bedtimeMin - 60) {
      if (nextNap > nowMin) {
        upcoming.push({
          id: `pred-nap-fill-${i}`,
          kind: "sleep",
          status: "upcoming",
          start: nextNap,
          predicted: { start: nextNap, dur: 75, basis: "tcb" },
        });
      }
      lastSleepEndMin = nextNap + 75;
    }
  }

  // Bedtime marker — only once a real bedtime exists (~8 weeks). Before then a
  // newborn has no fixed bedtime; night is a run of 2-3 hour cycles.
  if (band.hasBedtime) {
    upcoming.push({
      id: "pred-bedtime",
      kind: "bedtime",
      status: "upcoming",
      start: bedtimeMin,
      predicted: { start: bedtimeMin, dur: 30, basis: "tcb" },
    });
  }

  // De-dupe upcoming by (kind, start) within 10 min — avoid stacking near-equal predictions.
  const dedup: PlanEvent[] = [];
  for (const u of upcoming) {
    const collides = dedup.some((d) => d.kind === u.kind && Math.abs(d.start - u.start) < 10);
    if (!collides) dedup.push(u);
  }

  const all = [...planEvents, ...dedup].sort((a, b) => a.start - b.start);

  return {
    dayStartMin,
    dayEndMin,
    nowMin,
    events: all,
  };
}

